import React, { useMemo } from "react";
import {
  addMinutes,
  differenceInMinutes,
  endOfDay,
  endOfHour,
  format,
  getHours,
  startOfDay,
  startOfHour,
} from "date-fns";
import { Event } from "../../types/events";
import { Resource } from "../../types/resources";
import {
  SchedulerTheme,
  SchedulerViewComponentProps,
  ViewConfig,
  ViewInteractions,
} from "../../types/views";

/**
 * ResourceTimeGridView
 *
 * Vertical time-grid scheduler view for a single day where each column is a
 * resource (driver, technician, user, etc.) instead of a date. Mirrors the
 * Week view layout: hours stacked on the left, resources across the top.
 *
 * Events are placed in the column of every resource they're assigned to. Events
 * with no assigned resources go to a leading "Unassigned" column (toggle via
 * SHOW_UNASSIGNED_COLUMN).
 */

const HOUR_HEIGHT = 72;
const SLOT_MINUTES = 30;
const PIXELS_PER_MINUTE = HOUR_HEIGHT / 60;
const TIME_COLUMN_WIDTH_PX = 80;
const RESOURCE_COLUMN_MIN_WIDTH_PX = 160;
// Approximate height of the sticky header row (time label + resource label +
// subtitle + py-3 padding). Used to offset the sticky event-card text so it
// pins just below the header instead of sliding behind it during vertical
// scroll. If the header styling changes, nudge this value to match.
const HEADER_HEIGHT_PX = 76;

// Toggle: when true, events without an assigned resource render in a dedicated
// leading column. When false, those events are hidden from the view.
const SHOW_UNASSIGNED_COLUMN = true;
const UNASSIGNED_COLUMN_ID = "__unassigned__";

interface ResourceColumn {
  id: string;
  label: string;
  subtitle?: string;
  resource?: Resource;
  isUnassigned: boolean;
}

interface ResourceTimeGridViewProps extends SchedulerViewComponentProps {}

/**
 * Compute the visible time window for an event.
 *
 * Unlike the Day/Week TimeGridView (which caps trucking events to a short
 * "appointment" block), the Resource view represents how long each resource is
 * actually occupied — so we always use the raw start/end of the event. A
 * 4-hour trip renders as a 4-hour block in the driver's column.
 */
const getCalendarDisplayRange = (event: Event) => {
  const rawStart = new Date(event.timeRange.start);
  const rawEnd = new Date(event.timeRange.end);

  return {
    start: rawStart,
    end: rawEnd,
    labelStart: rawStart,
    labelEnd: rawEnd,
  };
};

/**
 * Group overlapping events into side-by-side lanes within a column so they
 * don't visually stack on top of each other.
 */
const createLanes = (columnEvents: Event[]) => {
  const sortedEvents = [...columnEvents].sort(
    (left, right) =>
      getCalendarDisplayRange(left).start.getTime() -
      getCalendarDisplayRange(right).start.getTime()
  );
  const laneEndTimes: number[] = [];

  return sortedEvents.map((event) => {
    const displayRange = getCalendarDisplayRange(event);
    const startTime = displayRange.start.getTime();
    const endTime = displayRange.end.getTime();
    let laneIndex = laneEndTimes.findIndex(
      (laneEndTime) => startTime >= laneEndTime
    );

    if (laneIndex === -1) {
      laneIndex = laneEndTimes.length;
      laneEndTimes.push(endTime);
    } else {
      laneEndTimes[laneIndex] = endTime;
    }

    return {
      event,
      laneIndex,
      laneCount: Math.max(laneEndTimes.length, 1),
    };
  });
};

/**
 * Default resource-id extractor used when the parent doesn't supply
 * getEventResourceIds. Handles both string IDs and `{ _id | id }` objects.
 */
const defaultGetEventResourceIds = (event: Event): (string | number)[] => {
  const assigned = (event as any).assignedResources;
  if (!Array.isArray(assigned)) return [];
  return assigned
    .map((entry: any) => {
      if (entry == null) return null;
      if (typeof entry === "string" || typeof entry === "number") return entry;
      return entry._id || entry.id || null;
    })
    .filter((value): value is string | number => value != null);
};

const ResourceTimeGridView: React.FC<ResourceTimeGridViewProps> = ({
  events,
  resources,
  getEventResourceIds,
  currentDate,
  startHour = 6,
  endHour = 20,
  onEventClick,
  onTimeSlotClick,
  onResourceClick,
  viewConfig,
  schedulerTheme,
}) => {
  const dayStart = useMemo(() => startOfDay(currentDate), [currentDate]);
  const dayEnd = useMemo(() => endOfDay(currentDate), [currentDate]);

  const resolveResourceIds = useMemo<
    (event: Event) => (string | number)[]
  >(() => {
    return getEventResourceIds || defaultGetEventResourceIds;
  }, [getEventResourceIds]);

  // Build the ordered list of columns (Unassigned + resources).
  const columns = useMemo<ResourceColumn[]>(() => {
    const resourceColumns: ResourceColumn[] = (resources || []).map(
      (resource) => ({
        id: String(resource.id),
        label: resource.name || "Unnamed",
        subtitle: (resource as any).type,
        resource,
        isUnassigned: false,
      })
    );

    if (!SHOW_UNASSIGNED_COLUMN) return resourceColumns;

    return [
      {
        id: UNASSIGNED_COLUMN_ID,
        label: "Unassigned",
        subtitle: "No resource",
        isUnassigned: true,
      },
      ...resourceColumns,
    ];
  }, [resources]);

  // Restrict to events that fall on the visible day.
  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      const displayRange = getCalendarDisplayRange(event);
      return displayRange.start < dayEnd && displayRange.end > dayStart;
    });
  }, [events, dayStart, dayEnd]);

  // Auto-expand the visible hour range to include any event that bleeds outside
  // the configured workday window.
  const visibleRange = useMemo(() => {
    if (!dayEvents.length) {
      return { startHour, endHour };
    }

    const earliestVisibleStart = dayEvents.reduce((earliest, event) => {
      const eventStart = getCalendarDisplayRange(event).start;
      return eventStart < earliest ? eventStart : earliest;
    }, getCalendarDisplayRange(dayEvents[0]).start);

    const latestVisibleEnd = dayEvents.reduce((latest, event) => {
      const eventEnd = getCalendarDisplayRange(event).end;
      return eventEnd > latest ? eventEnd : latest;
    }, getCalendarDisplayRange(dayEvents[0]).end);

    return {
      startHour: Math.min(startHour, getHours(startOfHour(earliestVisibleStart))),
      endHour: Math.max(endHour, getHours(endOfHour(latestVisibleEnd)) + 1),
    };
  }, [dayEvents, startHour, endHour]);

  const effectiveStartHour = visibleRange.startHour;
  const effectiveEndHour = visibleRange.endHour;
  const visibleMinutes = (effectiveEndHour - effectiveStartHour) * 60;
  const gridHeight =
    (effectiveEndHour - effectiveStartHour) * HOUR_HEIGHT || HOUR_HEIGHT;
  const slotCount = Math.max(1, visibleMinutes / SLOT_MINUTES);
  const slotHeight = SLOT_MINUTES * PIXELS_PER_MINUTE;

  // Map each column id to its pre-computed lane entries so the render loop
  // stays cheap and O(1) per column lookup.
  const laneMaps = useMemo(() => {
    const maps = new Map<
      string,
      Array<{ event: Event; laneIndex: number; laneCount: number }>
    >();

    columns.forEach((column) => {
      const columnEvents = dayEvents.filter((event) => {
        const ids = resolveResourceIds(event);
        if (column.isUnassigned) return ids.length === 0;
        return ids.some((id) => String(id) === column.id);
      });
      maps.set(column.id, createLanes(columnEvents));
    });

    return maps;
  }, [columns, dayEvents, resolveResourceIds]);

  const dayStartTime = useMemo(
    () => addMinutes(startOfDay(currentDate), effectiveStartHour * 60),
    [currentDate, effectiveStartHour]
  );
  const dayEndTime = useMemo(
    () => addMinutes(startOfDay(currentDate), effectiveEndHour * 60),
    [currentDate, effectiveEndHour]
  );

  const hasColumns = columns.length > 0;
  const gridTemplateColumns = `${TIME_COLUMN_WIDTH_PX}px repeat(${Math.max(
    columns.length,
    1
  )}, minmax(${RESOURCE_COLUMN_MIN_WIDTH_PX}px, 1fr))`;

  return (
    <div className="h-full w-full bg-white overflow-hidden flex flex-col">
      <div className="flex-1 overflow-auto">
        <div
          className="grid sticky top-0 z-20 border-b border-gray-200 bg-gray-50"
          style={{ gridTemplateColumns }}
        >
          {/* Corner cell: sticky to both top (via parent) and left so it stays
              visible in the top-left regardless of scroll direction. */}
          <div className="sticky left-0 z-10 bg-gray-50 border-r border-gray-200 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <div>Time</div>
            <div className="text-[11px] font-normal text-gray-400 normal-case">
              {format(currentDate, "EEE, MMM d")}
            </div>
          </div>
          {hasColumns ? (
            columns.map((column) => (
              <button
                key={column.id}
                type="button"
                disabled={column.isUnassigned || !column.resource}
                onClick={() =>
                  column.resource && onResourceClick?.(column.resource)
                }
                className={`text-left border-r border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 last:border-r-0 ${
                  column.isUnassigned || !column.resource
                    ? "cursor-default"
                    : "hover:bg-gray-100"
                }`}
              >
                <div className="truncate">{column.label}</div>
                {column.subtitle ? (
                  <div className="truncate text-xs font-normal text-gray-500">
                    {column.subtitle}
                  </div>
                ) : null}
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-sm text-gray-500">
              No resources to display
            </div>
          )}
        </div>

        <div
          className="grid relative"
          style={{
            height: gridHeight,
            minHeight: gridHeight,
            gridTemplateColumns,
          }}
        >
          {/* Body time column: sticky left-0 pins the hour labels while the
              user scrolls horizontally through resource columns. z-10 keeps it
              above resource column content but below the header row (z-20). */}
          <div className="sticky left-0 z-10 border-r border-gray-200 bg-gray-50">
            {Array.from(
              { length: effectiveEndHour - effectiveStartHour },
              (_, index) => {
                const hour = effectiveStartHour + index;
                return (
                  <div
                    key={hour}
                    className="border-b border-gray-200 px-3 text-xs text-gray-500"
                    style={{ height: HOUR_HEIGHT }}
                  >
                    <div className="pt-2 leading-none">
                      {format(new Date(2000, 0, 1, hour, 0, 0, 0), "h a")}
                    </div>
                  </div>
                );
              }
            )}
          </div>

          {hasColumns ? (
            columns.map((column) => {
              const columnLaneEntries = laneMaps.get(column.id) || [];

              return (
                <div
                  key={column.id}
                  className="relative border-r border-gray-200 last:border-r-0"
                  style={{ height: gridHeight, minHeight: gridHeight }}
                >
                  {Array.from({ length: slotCount }, (_, slotIndex) => {
                    const slotStart = addMinutes(
                      dayStartTime,
                      slotIndex * SLOT_MINUTES
                    );
                    const slotEnd = addMinutes(slotStart, SLOT_MINUTES);
                    // Pass the column's resource through so click handlers can
                    // know which resource the slot belongs to (Week/Day pass [] here).
                    const timeSlot = {
                      start: slotStart,
                      end: slotEnd,
                      isWorkHour: true,
                      isWeekend:
                        slotStart.getDay() === 0 || slotStart.getDay() === 6,
                      events: [],
                      resources:
                        column.resource && !column.isUnassigned
                          ? [column.resource]
                          : [],
                    };

                    return (
                      <button
                        key={`${column.id}-${slotIndex}`}
                        type="button"
                        className="absolute inset-x-0 border-b border-gray-100 hover:bg-blue-50/70"
                        style={{
                          top: slotIndex * slotHeight,
                          height: slotHeight,
                          backgroundColor:
                            schedulerTheme?.getGridCellColor?.(timeSlot) ||
                            "#ffffff",
                        }}
                        onClick={(event) =>
                          onTimeSlotClick?.(
                            timeSlot,
                            event.currentTarget as unknown as HTMLElement
                          )
                        }
                      />
                    );
                  })}

                  {Array.from(
                    { length: effectiveEndHour - effectiveStartHour },
                    (_, hourIndex) => (
                      <div
                        key={`${column.id}-hour-${hourIndex}`}
                        className="absolute inset-x-0 border-b border-gray-200 pointer-events-none"
                        style={{
                          top: hourIndex * HOUR_HEIGHT,
                          height: HOUR_HEIGHT,
                        }}
                      />
                    )
                  )}

                  {columnLaneEntries.map(({ event, laneIndex, laneCount }) => {
                    const displayRange = getCalendarDisplayRange(event);
                    const eventStart = displayRange.start;
                    const eventEnd = displayRange.end;
                    const displayStart =
                      eventStart > dayStartTime ? eventStart : dayStartTime;
                    const displayEnd =
                      eventEnd < dayEndTime ? eventEnd : dayEndTime;

                    if (displayEnd <= displayStart) return null;

                    const topMinutes = differenceInMinutes(
                      displayStart,
                      dayStartTime
                    );
                    const durationMinutes = Math.max(
                      differenceInMinutes(displayEnd, displayStart),
                      SLOT_MINUTES
                    );
                    const top = topMinutes * PIXELS_PER_MINUTE;
                    const eventHeight = Math.max(
                      durationMinutes * PIXELS_PER_MINUTE,
                      slotHeight
                    );
                    const width = `calc(${100 / laneCount}% - 8px)`;
                    const left = `calc(${(laneIndex / laneCount) * 100}% + 4px)`;
                    const eventColor =
                      schedulerTheme?.getEventColor?.(event) || "#2563eb";

                    return (
                      <button
                        key={`${event.id}-${column.id}-${laneIndex}`}
                        type="button"
                        // flex + flex-col overrides the browser's internal
                        // button layout (which vertically centers a single
                        // child), so the sticky content sits at the top of
                        // tall event cards instead of drifting to the middle.
                        className="absolute rounded-md text-left shadow-sm flex flex-col"
                        style={{
                          top,
                          left,
                          width,
                          height: eventHeight,
                          backgroundColor: eventColor,
                          color: schedulerTheme?.eventTextColor || "#ffffff",
                          // `clip-path` gives us rounded-corner clipping
                          // WITHOUT turning the button into a scroll container
                          // (which `overflow: hidden` would do and would break
                          // the sticky child below).
                          clipPath: "inset(0 round 0.375rem)",
                        }}
                        onClick={(buttonEvent) =>
                          onEventClick?.(
                            event,
                            buttonEvent.currentTarget as unknown as HTMLElement,
                            viewConfig as ViewConfig
                          )
                        }
                      >
                        {/* Sticky inner wrapper: pins title+time near the top
                            of the card as the user scrolls past a tall event.
                            The top offset matches the header height so the
                            text stays below the sticky header instead of
                            hiding behind it. IMPORTANT: the button must NOT
                            have overflow:hidden, or it becomes a scroll
                            container and position:sticky stops working. */}
                        <div
                          className="sticky px-2 py-1"
                          style={{ top: HEADER_HEIGHT_PX }}
                        >
                          <div className="truncate text-xs font-semibold">
                            {event.title}
                          </div>
                          <div className="truncate text-[11px] opacity-80">
                            {format(displayRange.labelStart, "h:mm a")}
                            {displayRange.labelStart.getTime() !==
                            displayRange.labelEnd.getTime()
                              ? ` - ${format(displayRange.labelEnd, "h:mm a")}`
                              : ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })
          ) : (
            <div className="flex items-center justify-center text-sm text-gray-500">
              Add resources to see scheduled work for{" "}
              {format(currentDate, "MMM d")}.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Exported helpers for unit testing (column derivation, event partitioning).
// Marked with leading underscore to signal "internal/test-only" usage.
export const _internals = {
  SHOW_UNASSIGNED_COLUMN,
  UNASSIGNED_COLUMN_ID,
  defaultGetEventResourceIds,
  /**
   * Build the ordered column list (Unassigned + resources). Pure helper so
   * tests can validate column derivation without rendering React.
   */
  buildColumns: (
    resources: Resource[] | undefined,
    showUnassigned = SHOW_UNASSIGNED_COLUMN
  ): ResourceColumn[] => {
    const resourceColumns: ResourceColumn[] = (resources || []).map(
      (resource) => ({
        id: String(resource.id),
        label: resource.name || "Unnamed",
        subtitle: (resource as any).type,
        resource,
        isUnassigned: false,
      })
    );

    if (!showUnassigned) return resourceColumns;

    return [
      {
        id: UNASSIGNED_COLUMN_ID,
        label: "Unassigned",
        subtitle: "No resource",
        isUnassigned: true,
      },
      ...resourceColumns,
    ];
  },
  /**
   * Partition events into a Map<columnId, Event[]>. Mirrors the production
   * filter so tests can assert event-to-column placement (multi-resource
   * events appear in every matching column; unassigned events bucket into
   * the Unassigned column when enabled).
   */
  partitionEventsByColumn: (
    columns: ResourceColumn[],
    events: Event[],
    getResourceIds: (event: Event) => (string | number)[] = defaultGetEventResourceIds
  ): Map<string, Event[]> => {
    const map = new Map<string, Event[]>();
    columns.forEach((column) => {
      const filtered = events.filter((event) => {
        const ids = getResourceIds(event);
        if (column.isUnassigned) return ids.length === 0;
        return ids.some((id) => String(id) === column.id);
      });
      map.set(column.id, filtered);
    });
    return map;
  },
};

export default ResourceTimeGridView;
